import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete } from '@mui/material';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

import { marketingChannelOptions } from '../../utils/marketingChannels';
import { Title } from '../../components/atoms/title';

import api from '../../services/api';
import { useUniversities } from '../../hooks/universities';

const formatDate = dateString => {
  const formattedDate = format(new Date(dateString), "yyyy-MM-dd'T'HH:mm");

  return formattedDate;
};

const schema = yup
  .object({
    partner: yup.string().required('Parceiro é obrigatório'),
    experiment_name: yup.string().required('Nome do experimento é obrigatório'),
    token: yup
      .string()
      .matches(
        /^[A-Za-z\d]{3,40}$/,
        'O código de convite deve somente contar letras e números entre 3 e 40 caracteres.'
      )
      .required('Código de convite é obrigatório'),
    limit: yup
      .number()
      .typeError('Número de convites precisa ser um número')
      .required('Número de convites é obrigatório'),
    budget: yup.number().typeError('Budget precisa ser um número').required('Budget é obrigatório'),
    channel: yup.string().required('Canal é obrigatório'),

    expiration: yup
      .date()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .required('Campo obrigatório'),

    // event_name: yup
    //   .string()
    //   .max(35, 'O nome do evento deve ser menor que 35 caracteres')
    //   .trim()
    //   .when('channel', {
    //     is: 'In-App Events',
    //     then: yup.string().required('Nome do evento é obrigatório'),
    //   }),
    // event_date: yup.date().when('channel', {
    //   is: 'In-App Events',
    //   then: yup.date().typeError('Data do evento é obrigatória'),
    // }),

    // event_expiration: yup.date().when('channel', {
    //   is: 'In-App Events',
    //   then: yup
    //     .date()
    //     .typeError('Data de expiração do evento é obrigatória')
    //     .test({
    //       name: 'max',
    //       message: 'Precisa ser depois da data anterior',
    //       test(value, testContext) {
    //         if (testContext.parent.event_expiration && value) {
    //           return new Date(testContext.parent.event_date) < new Date(value);
    //         }
    //         return true;
    //       },
    //     }),
    // }),

    // event_short_name: yup
    //   .string()
    //   .max(15, 'A abreviação deve ser menor que 15 caracteres')
    //   .trim()
    //   .when('channel', {
    //     is: 'In-App Events',
    //     then: yup.string().required('Abreviação do evento é obrigatória'),
    //   }),
  })
  .required();

export function PartnerInviteEdit() {
  const { partners } = useUniversities();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      partner: '',
      experiment_name: 'Não possui experiment_name',
      token: '',
      channel: '',
      expiration: '',
    },
  });

  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedPartnerInvites, setSelectedPartnerInvites] = useState([]);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [updating, setUpdating] = useState(false);

  const resetFields = () => {
    reset();
    setSelectedPartner(null);
    setSelectedPartnerInvites([]);
    setSelectedInvite(null);
  };

  const onSubmit = handleSubmit(async data => {
    try {
      setUpdating(true);

      const body = {
        id: selectedInvite.id,
        partner_id: selectedPartner.id,
        experiment_name: data.experiment_name,
        token: data.token,
        limit: Number(data.limit),
        budget: Number(data.budget),
        channel: data.channel,
        expiration: data.expiration,
      };

      if (selectedInvite.event) {
        body.event = {
          id: selectedInvite.event.id,
          name: data.event_name,
          short_name: data.event_short_name,
          date: data.event_date,
          expiration: data.event_expiration,
        };
      }
      await api.put('/invite', body);

      reset();
      setSelectedPartnerInvites(state =>
        state.map(invite => {
          if (invite.id === selectedInvite.id) {
            return body;
          }
          return invite;
        })
      );

      setValue('partner', selectedPartner.name);
      setSelectedInvite(null);

      toast('Experimento atualizado com sucesso!', { type: 'success' });
    } catch (error) {
      toast('Erro ao atualizar o experimento.', { type: 'error' });
    } finally {
      setUpdating(false);
    }
  });

  useEffect(() => {
    const fetchPartnerInvites = async () => {
      if (selectedPartner?.id) {
        try {
          const { data } = await api.get(`/partners/${selectedPartner.id}/invites`);

          setSelectedPartnerInvites(data);
        } catch (error) {
          toast('Erro ao buscar experimentos deste parceiro.', {
            type: 'error',
          });
        }
      }
    };

    fetchPartnerInvites();
  }, [selectedPartner]);

  return (
    <Grid container spacing={2}>
      <Grid item>
        <Title>Edição de convites</Title>
      </Grid>

      <Grid xs={12} md={12} item>
        <Autocomplete
          options={partners}
          getOptionLabel={option => option.name}
          getOptionKey={option => option.id}
          onChange={(e, option, reason) => {
            if (reason === 'clear') {
              resetFields();
              return;
            }

            if (option.name) {
              setValue('partner', option.name);
              setSelectedPartner(option);
            } else {
              setValue('partner', '');
            }

            setSelectedInvite(null);
            setSelectedPartnerInvites([]);
          }}
          renderInput={params => <TextField fullWidth {...params} label='Parceiro' variant='outlined' />}
        />
        <Typography variant='inherit' color='textSecondary'>
          {errors.partner?.message}
        </Typography>
      </Grid>

      {selectedPartnerInvites.length > 0 && (
        <Grid xs={12} md={12} item>
          <Autocomplete
            options={selectedPartnerInvites}
            value={selectedInvite}
            getOptionLabel={option => option.experiment_name ?? 'Não possui experiment_name'}
            getOptionKey={option => option.id}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(e, option, reason) => {
              if (reason === 'clear') {
                reset();
                setSelectedInvite(null);
                return;
              }

              if (option.token) {
                setSelectedInvite(option);

                setValue('experiment_name', option.experiment_name);
                setValue('token', option.token);
                setValue('limit', option.limit);
                setValue('budget', option.budget);
                setValue('channel', option.channel);
                setValue('expiration', formatDate(option.expiration));

                if (option.event) {
                  const { event } = option;
                  setValue('event_date', formatDate(event.date));
                  setValue('event_expiration', formatDate(event.expiration));
                  setValue('event_short_name', event.short_name);
                  setValue('event_name', event.name);
                }
              } else {
                setSelectedInvite(null);
              }
            }}
            renderInput={params => (
              <TextField
                fullWidth
                value={selectedInvite?.experiment_name}
                {...params}
                label='Experimento'
                variant='outlined'
              />
            )}
          />
          <Typography variant='inherit' color='textSecondary'>
            {errors.selectedInvite?.message}
          </Typography>
        </Grid>
      )}

      {selectedInvite && (
        <>
          <Grid item xs={12} sm={12}>
            <TextField
              required
              id='experiment_name'
              name='experiment_name'
              label='Nome do experimento'
              fullWidth
              {...register('experiment_name')}
              error={!!errors.experiment_name}
              variant='outlined'
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Typography variant='inherit' color='textSecondary'>
              {errors.experiment_name?.message}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={12}>
            <TextField
              required
              id='token'
              name='token'
              label='Código de convite'
              fullWidth
              {...register('token')}
              error={!!errors.token}
              variant='outlined'
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Typography variant='inherit' color='textSecondary'>
              {errors.token?.message}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              required
              id='limit'
              name='limit'
              label='Número de convites'
              fullWidth
              {...register('limit')}
              error={!!errors.limit}
              variant='outlined'
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Typography variant='inherit' color='textSecondary'>
              {errors.limit?.message}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              required
              id='budget'
              name='budget'
              label='Budget'
              fullWidth
              {...register('budget')}
              error={!!errors.budget}
              variant='outlined'
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Typography variant='inherit' color='textSecondary'>
              {errors.budget?.message}
            </Typography>
          </Grid>

          <Grid xs={12} md={12} item>
            <Autocomplete
              options={marketingChannelOptions}
              getOptionLabel={option => option}
              defaultValue={selectedInvite.channel}
              onChange={(e, option, reason) => {
                if (reason === 'clear') {
                  setValue('channel', '');

                  return;
                }

                if (option) {
                  setValue('channel', option);
                } else {
                  setValue('channel', '');
                }
              }}
              renderInput={params => <TextField fullWidth {...params} label='Canal' variant='outlined' />}
            />
            <Typography variant='inherit' color='textSecondary'>
              {errors.channel?.message}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={12}>
            <TextField
              required
              type='datetime-local'
              InputLabelProps={{
                shrink: true,
              }}
              id='expiration'
              name='expiration'
              label='Data de expiração'
              fullWidth
              variant='outlined'
              {...register('expiration')}
              error={!!errors.expiration}
            />
            <Typography variant='inherit' color='textSecondary'>
              {errors.expiration?.message}
            </Typography>
          </Grid>
          {/* {selectedInvite.event && (
            <>
              <Grid xs={12} md={8} item>
                <TextField
                  style={{ width: '100%' }}
                  label='Nome do evento'
                  variant='outlined'
                  {...register('event_name')}
                  error={!!errors.event_name}
                />

                <Typography variant='inherit' color='textSecondary'>
                  {errors.event_name?.message}
                </Typography>
              </Grid>

              <Grid xs={12} md={4} item>
                <TextField
                  style={{ width: '100%' }}
                  label='Abreviação do evento'
                  variant='outlined'
                  {...register('event_short_name')}
                  error={!!errors.event_short_name}
                />

                <Typography variant='inherit' color='textSecondary'>
                  {errors.event_short_name?.message}
                </Typography>
              </Grid>

              <Grid xs={12} md={6} item>
                <TextField
                  type='datetime-local'
                  style={{ width: '100%' }}
                  label='Data de início do evento'
                  variant='outlined'
                  {...register('event_date')}
                  error={!!errors.event_date}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />

                <Typography variant='inherit' color='textSecondary'>
                  {errors.event_date?.message}
                </Typography>
              </Grid>

              <Grid xs={12} md={6} item>
                <TextField
                  type='datetime-local'
                  style={{ width: '100%' }}
                  label='Fim do evento'
                  variant='outlined'
                  {...register('event_expiration')}
                  error={!!errors.event_expiration}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />

                <Typography variant='inherit' color='textSecondary'>
                  {errors.event_expiration?.message}
                </Typography>
              </Grid>
              <Typography variant='inherit' color='textSecondary'>
                {errors}
              </Typography>
            </>
          )} */}
        </>
      )}

      <Box m='auto' mt={3}>
        <Button
          onClick={handleSubmit(onSubmit)}
          color='primary'
          type='submit'
          variant='contained'
          disabled={!selectedInvite || updating}
        >
          {updating ? 'Atualizando...' : 'Atualizar'}
        </Button>
      </Box>
    </Grid>
  );
}
